import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Container, IconButton, Snackbar, Typography, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";
import DocumentMaximize from "../../assets/Icons/Document_maximize.svg";
import DocumentDownload from "../../assets/Icons/Document_download.svg";

const DOCUMENTS_CMS_SECTION_ID = 118;

const safeDownloadBaseName = (label) => {
  const base = (label || "document").replace(/[/\\?%*:|"<>]/g, "-").replace(/\s+/g, " ").trim().slice(0, 80);
  return base || "document";
};

const extensionFromUrl = (url) => {
  try {
    const path = new URL(url).pathname;
    const m = path.match(/\.(png|jpe?g|webp|gif|pdf)$/i);
    return m ? m[0].toLowerCase() : ".png";
  } catch {
    return ".png";
  }
};

const isProbablyIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const iOSDevice = /iPad|iPhone|iPod/i.test(platform) || /iPad|iPhone|iPod/i.test(ua);
  const iPadOS13Plus = /Mac/i.test(platform) && typeof document !== "undefined" && "ontouchend" in document;
  return iOSDevice || iPadOS13Plus;
};

const toFileForShare = (blob, filename, fallbackType = "application/octet-stream") => {
  const type = (blob && blob.type) || fallbackType;
  try {
    return new File([blob], filename, { type });
  } catch {
    // Older Safari: File constructor can be missing/buggy
    const f = new Blob([blob], { type });
    f.name = filename;
    return f;
  }
};

const saveBlobWithFilePicker = async (blob, filename) => {
  if (typeof window === "undefined") return false;
  if (typeof window.showSaveFilePicker !== "function") return false;
  try {
    const ext = (filename.split(".").pop() || "").toLowerCase();
    const mime = (blob && blob.type) || "application/octet-stream";
    const types =
      ext && mime
        ? [
            {
              description: "File",
              accept: { [mime]: [`.${ext}`] },
            },
          ]
        : undefined;

    const handle = await window.showSaveFilePicker({
      suggestedName: filename,
      excludeAcceptAllOption: false,
      types,
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch {
    return false;
  }
};

const triggerBlobDownload = (blob, filename) => {
  const mime = (blob && blob.type) || "";
  const asFile =
    mime.startsWith("image/") || mime === "image/svg+xml"
      ? new Blob([blob], { type: "application/octet-stream" })
      : blob;
  const objUrl = URL.createObjectURL(asFile);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
};

const fetchBlobViaXHR = (url) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.responseType = "blob";
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300 && xhr.response) resolve(xhr.response);
      else reject(new Error(`xhr ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("xhr network"));
    xhr.send();
  });

/** Same-origin URLs: no crossOrigin (canvas not tainted). Remote S3: anonymous + CORS on bucket. */
const fetchBlobViaImageCanvas = (url) =>
  new Promise((resolve, reject) => {
    let sameOrigin = false;
    try {
      sameOrigin = new URL(url).origin === window.location.origin;
    } catch {
      reject(new Error("bad url"));
      return;
    }
    const img = new Image();
    if (!sameOrigin) img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("no canvas context"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error("toBlob null"));
          },
          "image/png",
          0.92
        );
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("image load"));
    img.src = url;
  });

/** Use Vite/preview proxy on localhost even for production bundles (`vite preview` has DEV=false). */
const defaultS3CmsProxyPrefix = () => {
  if (typeof window === "undefined") return null;
  if (import.meta.env.DEV) return "/__s3_cms";
  const h = window.location.hostname;
  if (h === "localhost" || h === "127.0.0.1") return "/__s3_cms";
  return null;
};

/**
 * Same-origin proxy path (Vite dev/preview / nginx in prod) so fetch() can read S3 files as blob and trigger download.
 * Set VITE_S3_CMS_DOWNLOAD_PROXY=/__s3_cms on real production hosts when nginx proxies /__s3_cms → S3.
 */
const buildS3ProxiedSameOriginUrl = (originalUrl) => {
  const envProxy = String(import.meta.env.VITE_S3_CMS_DOWNLOAD_PROXY || "").trim();
  const prefix = envProxy || defaultS3CmsProxyPrefix();
  if (!prefix || typeof window === "undefined") return null;
  try {
    const u = new URL(originalUrl);
    if (!u.hostname.includes("amazonaws.com")) return null;
    if (prefix.startsWith("http")) {
      const base = new URL(prefix);
      return `${base.origin}${String(base.pathname).replace(/\/$/, "")}${u.pathname}${u.search}`;
    }
    return `${window.location.origin}${String(prefix).replace(/\/$/, "")}${u.pathname}${u.search}`;
  } catch {
    return null;
  }
};

const collectBlobFetchCandidates = (imageUrl) => {
  const proxied = buildS3ProxiedSameOriginUrl(imageUrl);
  const list = [];
  if (proxied) list.push(proxied);
  if (!list.includes(imageUrl)) list.push(imageUrl);
  return list;
};

const fetchFirstWorkingBlob = async (candidates) => {
  let lastErr;
  for (const u of candidates) {
    try {
      const res = await fetch(u, { mode: "cors", credentials: "omit", cache: "no-cache" });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      if (blob && blob.size > 0) return blob;
      throw new Error("empty");
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("fetch failed");
};

const xhrBlobFirstWorking = async (candidates) => {
  let lastErr;
  for (const u of candidates) {
    try {
      return await fetchBlobViaXHR(u);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("xhr failed");
};

const canvasBlobFirstWorking = async (candidates) => {
  let lastErr;
  for (const u of candidates) {
    try {
      const b = await fetchBlobViaImageCanvas(u);
      if (b && b.size > 0) return b;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("canvas failed");
};

const DocumentsPage = () => {
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const isMobile = useMediaQuery("(max-width: 767.95px)");
  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const [previewSrc, setPreviewSrc] = useState(null);
  const [downloadHint, setDownloadHint] = useState(null);

  const handleDownloadDocument = useCallback(async (imageUrl, label) => {
    if (!imageUrl) return;
    const ext = extensionFromUrl(imageUrl);
    const filename = `${safeDownloadBaseName(label)}${ext}`;
    const candidates = collectBlobFetchCandidates(imageUrl);

    const tryBlob = async (blob, downloadName = filename) => {
      if (!blob || blob.size === 0) throw new Error("empty blob");

      // iOS Safari: "download" is unreliable; Share Sheet is the best UX (Save Image / Save to Files).
      if (isProbablyIOS() && navigator?.share) {
        const fileLike = toFileForShare(blob, downloadName);
        const canShareFiles =
          typeof navigator.canShare === "function" ? navigator.canShare({ files: [fileLike] }) : true;
        if (canShareFiles) {
          try {
            await navigator.share({ files: [fileLike], title: downloadName });
            return;
          } catch (e) {
            const name = e && e.name;
            if (name === "AbortError") return;
            // Share failed (e.g. in-app browser): fall through to anchor download attempt.
          }
        }
      }

      // Android mobile only: Chrome can show "Save as…". Desktop must use direct download (no picker).
      if (isMobile && !isProbablyIOS() && (await saveBlobWithFilePicker(blob, downloadName))) return;

      triggerBlobDownload(blob, downloadName);
    };

    try {
      await tryBlob(await fetchFirstWorkingBlob(candidates));
      return;
    } catch {
      /* continue */
    }

    try {
      await tryBlob(await xhrBlobFirstWorking(candidates));
      return;
    } catch {
      /* continue */
    }

    try {
      const pngBlob = await canvasBlobFirstWorking(candidates);
      const pngName = ext.toLowerCase() === ".pdf" ? filename : `${safeDownloadBaseName(label)}.png`;
      await tryBlob(pngBlob, pngName);
      return;
    } catch {
      /* continue */
    }

    if (isMobile) {
      window.open(imageUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setDownloadHint(
      "Could not download automatically: the browser blocks reading the S3 file from this site without CORS or a same-origin proxy (/__s3_cms). Configure your host or bucket, then try again."
    );
  }, [isMobile]);

  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  const { pageTitle, pageDescription, documents } = useMemo(() => {
    const sections = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const section = sections.find((s) => s?.id === DOCUMENTS_CMS_SECTION_ID);
    const related = Array.isArray(section?.relatedCMS) ? section.relatedCMS : [];
    const pageTitle = (section?.title || "").trim();
    const pageDescription = (section?.description || "").trim();
    const documents = related
      .filter((r) => r && (r.status === 1 || r.status === true))
      .map((r) => ({
        id: r.id,
        label: (r.title || "").trim(),
        image: (r.image || "").trim(),
      }))
      .filter((d) => d.image);
    return { pageTitle, pageDescription, documents };
  }, [cmsHome]);

  return (
    <Box>

      <Container maxWidth={false} disableGutters sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto", mb: isMobile ? "30px" : "60px", mt: isMobile ? "0px" : "30px" }}>
        <Box
          sx={{
            background: "#F7F7F7",
            borderRadius: isMobile ? "0px" : "26px",
            px: isMobile ? "0px" : "30px",
            py: isMobile ? "0px" : "30px",
          }}
        >
          {/* Mobile background panel (Figma shows full-width light panel) */}
          {isMobile ? (
            <Box
              sx={{
                background: "#F7F7F7",
                width: "100%",
                px: "16px",
                pt: "20px",
                pb: "18px",
              }}
            >
              {/* Header */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "13px" }}>
                  <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: "#8EA946" }} />
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "10px",
                      lineHeight: "10px",
                      letterSpacing: "2px",
                      textTransform: "uppercase",
                      color: "#131313",
                    }}
                  >
                    Documents
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "18px",
                    lineHeight: "21.6px",
                    letterSpacing: "-0.36px",
                    color: "#1D1D1B",
                    textTransform: "capitalize",
                    maxWidth: "100%",
                  }}
                >
                  {pageTitle || "\u00a0"}
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "20px",
                    letterSpacing: "-0.24px",
                    color: "#7D7D7D",
                    width: "328px",
                    maxWidth: "100%",
                  }}
                >
                  {pageDescription || "\u00a0"}
                </Typography>
              </Box>
            </Box>
          ) : null}

          {/* Header */}
          <Box sx={{ display: isMobile ? "none" : "flex", flexDirection: "column", gap: "12px", mb: "24px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", height: isMobile ? "16px" : "18.973px" }}>
              <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: "#8EA946" }} />
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "18.2px",
                  letterSpacing: "2.1px",
                  textTransform: "uppercase",
                  color: "#131313",
                }}
              >
                Documents
              </Typography>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "36px",
                lineHeight: 1.4,
                letterSpacing: "-0.36px",
                color: "#1D1D1B",
                textTransform: "capitalize",
              }}
            >
              {pageTitle || "\u00a0"}
            </Typography>

            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "24px",
                color: "#7D7D7D",
                maxWidth: isDesktop ? "900px" : "100%",
              }}
            >
              {pageDescription || "\u00a0"}
            </Typography>
          </Box>

          {!cmsHome ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: "48px" }}>
              <CircularProgress sx={{ color: "#8EA946" }} />
            </Box>
          ) : null}

          {/* Desktop grid / Mobile horizontal scroll */}
          <Box
            sx={{
              display: isMobile ? "flex" : "grid",
              gridTemplateColumns: isMobile ? undefined : "repeat(3, minmax(0, 1fr))",
              gap: isMobile ? "16px" : "24px",
              overflowX: isMobile ? "auto" : "visible",
              overflowY: "hidden",
              WebkitOverflowScrolling: isMobile ? "touch" : undefined,
              px: isMobile ? "16px" : "0px",
              pb: isMobile ? "20px" : "0px",
              width: "100%",
              "&::-webkit-scrollbar": { display: isMobile ? "none" : "initial" },
              scrollbarWidth: isMobile ? "none" : "auto",
            }}
          >
            {cmsHome && documents.length === 0 ? (
              <Box
                sx={{
                  gridColumn: isMobile ? undefined : "1 / -1",
                  width: isMobile ? "100%" : "auto",
                  flexShrink: 0,
                  py: "24px",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ color: "#7D7D7D", fontSize: "14px" }}>No documents available.</Typography>
              </Box>
            ) : null}

            {documents.map((doc) => (
              <Box
                key={doc.id}
                sx={{
                  background: "#FFFFFF",
                  borderRadius: isMobile ? "10px" : "18px",
                  p: isMobile ? "14px" : undefined,
                  px: isMobile ? undefined : "16px",
                  pt: isMobile ? undefined : "16px",
                  pb: isMobile ? undefined : "20px",
                  boxShadow: isMobile ? "0px 7.194px 9.353px rgba(0,0,0,0.04)" : "0px 10px 13px rgba(0,0,0,0.04)",
                  flexShrink: isMobile ? 0 : undefined,
                  width: isMobile ? "304px" : "auto",
                }}
              >
                <Box
                  sx={{
                    background: "#F5F8ED",
                    border: "0.267px solid #D1DFAD",
                    borderRadius: "8px",
                    p: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: isMobile ? "12px" : "16px",
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "12px", lineHeight: "18px", color: "#8EA946", textTransform: "capitalize" }}>
                    {doc.label}
                  </Typography>
                </Box>

                <Box sx={{ position: "relative" }}>
                  <Box
                    sx={{
                      width: isMobile ? "276px" : "100%",
                      height: isMobile ? "391.418px" : undefined,
                      aspectRatio: isMobile ? undefined : "385 / 546",
                      borderRadius: isMobile ? "16.853px" : "23.509px",
                      overflow: "hidden",
                      border: isMobile ? "1.404px solid rgba(0,0,0,0.1)" : "1.959px solid rgba(0,0,0,0.1)",
                      position: "relative",
                    }}
                  >
                    <Box component="img" src={doc.image} alt={doc.label} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <Box sx={{ position: "absolute", inset: 0, background: isMobile ? "rgba(17,17,17,0.06)" : "rgba(17,17,17,0.04)" }} />
                  </Box>

                  <Box
                    sx={{
                      position: "absolute",
                      right: isMobile ? "12px" : "16px",
                      bottom: isMobile ? "12px" : "16px",
                      display: "flex",
                      gap: isMobile ? "10px" : "14px",
                    }}
                  >
                    <IconButton
                      onClick={() => setPreviewSrc(doc.image)}
                      sx={{
                        width: isMobile ? "40px" : "46px",
                        height: isMobile ? "40px" : "46px",
                        borderRadius: isMobile ? "20px" : "23px",
                        background: "#FFFFFF",
                        boxShadow: isMobile ? "0px 3.478px 10.435px rgba(255,255,255,0.1)" : "0px 4px 12px rgba(255,255,255,0.1)",
                        "&:hover": { background: "#FFFFFF" },
                      }}
                    >
                      <Box component="img" src={DocumentMaximize} alt="Maximize" sx={{ width: isMobile ? "17.391px" : "20px", height: isMobile ? "17.391px" : "20px" }} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDownloadDocument(doc.image, doc.label)}
                      sx={{
                        width: isMobile ? "40px" : "46px",
                        height: isMobile ? "40px" : "46px",
                        borderRadius: isMobile ? "20px" : "23px",
                        background: "#FFFFFF",
                        boxShadow: isMobile ? "0px 3.478px 10.435px rgba(255,255,255,0.1)" : "0px 4px 12px rgba(255,255,255,0.1)",
                        "&:hover": { background: "#FFFFFF" },
                      }}
                    >
                      <Box component="img" src={DocumentDownload} alt="Download" sx={{ width: isMobile ? "16px" : "20px", height: isMobile ? "16px" : "20px" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Simple preview (same as maximize intent) */}
          {previewSrc ? (
            <Box
              onClick={() => setPreviewSrc(null)}
              sx={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.6)",
                zIndex: 2000,
                display: "grid",
                placeItems: "center",
                p: "24px",
              }}
            >
              <Box
                component="img"
                src={previewSrc}
                alt="Preview"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  borderRadius: "12px",
                  objectFit: "contain",
                  background: "#FFFFFF",
                }}
              />
            </Box>
          ) : null}
        </Box>
      </Container>

      <Snackbar
        open={Boolean(downloadHint)}
        onClose={() => setDownloadHint(null)}
        autoHideDuration={9000}
        message={downloadHint}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default DocumentsPage;

